import { UNSAFE_PortalProvider } from 'react-aria'
import { OverlayArrow as AriaOverlayArrow, Tooltip as AriaTooltip, TooltipTrigger as AriaTooltipTrigger } from 'react-aria-components'

import { cx } from '@utils/cx.ts'

import type { ReactNode, RefObject } from 'react'
import type {
  TooltipProps as AriaTooltipProps,
  TooltipTriggerComponentProps as AriaTooltipTriggerComponentProps
} from 'react-aria-components'

interface TooltipProps extends AriaTooltipTriggerComponentProps, Omit<AriaTooltipProps, 'children'> {
    /**
     * The title of the tooltip.
     */
    title: ReactNode
    /**
     * The description of the tooltip.
     */
    description?: ReactNode
    /**
     * Whether to show the arrow on the tooltip.
     *
     * @default false
     */
    arrow?: boolean
    /**
     * Delay in milliseconds before the tooltip is shown.
     *
     * @default 300
     */
    delay?: number
    /**
     * The container element in which the tooltip will be rendered.
     * By default, tooltips are rendered in document.body.
     * Use this to render the tooltip inside a specific container.
     */
    portalContainer?: RefObject<HTMLElement | null> | null
}

/**
 * Tooltip component for displaying brief, informative text on hover or focus.
 *
 * Unlike the Popover component, Tooltip is intended for non-interactive, concise content.
 * Use it to provide context or explanations for UI elements.
 *
 * Children must be focusable elements (e.g., React-Aria components).
 * To use a non-focusable element as a trigger, wrap it with the `Focusable` component.
 */
function Tooltip({
  title,
  description,
  children,
  arrow = false,
  delay = 300,
  closeDelay = 0,
  trigger,
  isDisabled,
  isOpen,
  defaultOpen,
  offset = 6,
  crossOffset,
  placement = 'top',
  onOpenChange,
  portalContainer,
  ...tooltipProps
}: TooltipProps) {
  const isTopOrBottomLeft = ['top left', 'top end', 'bottom left', 'bottom end'].includes(placement)
  const isTopOrBottomRight = ['top right', 'top start', 'bottom right', 'bottom start'].includes(placement)
  // Set negative cross offset for left and right placement to visually balance the tooltip.
  const calculatedCrossOffset = isTopOrBottomLeft ? -12 : isTopOrBottomRight ? 12 : 0

  const tooltipContent = (
    <AriaTooltipTrigger {...{ trigger, delay, closeDelay, isDisabled, isOpen, defaultOpen, onOpenChange }}>
      {children}

      <AriaTooltip
        {...tooltipProps}
        offset={offset}
        placement={placement}
        crossOffset={crossOffset ?? calculatedCrossOffset}
        className={({ isEntering, isExiting }) => cx(isEntering && 'ease-out animate-in', isExiting && `
          ease-in animate-out
        `)}
      >
        {({ isEntering, isExiting }) => (
          <div
            className={cx(
              `
                z-50 flex max-w-xs origin-(--trigger-anchor-point) flex-col items-start gap-1 rounded-lg
                bg-primary-solid px-3 shadow-lg will-change-transform
              `,
              description ? 'py-3' : 'py-2',

              isEntering &&
                            `
                              ease-out animate-in fade-in zoom-in-95
                              in-placement-left:slide-in-from-right-0.5
                              in-placement-right:slide-in-from-left-0.5
                              in-placement-top:slide-in-from-bottom-0.5
                              in-placement-bottom:slide-in-from-top-0.5
                            `,
              isExiting &&
                            `
                              ease-in animate-out fade-out zoom-out-95
                              in-placement-left:slide-out-to-right-0.5
                              in-placement-right:slide-out-to-left-0.5
                              in-placement-top:slide-out-to-bottom-0.5
                              in-placement-bottom:slide-out-to-top-0.5
                            `
            )}
          >
            <span className="text-xs font-semibold text-white">{title}</span>

            {description && <div className="w-full text-xs font-medium text-tooltip-supporting-text">{description}</div>}

            {arrow && (
              <AriaOverlayArrow>
                <svg
                  viewBox="0 0 100 100"
                  className={`
                    size-2.5 fill-bg-primary-solid
                    in-placement-left:-rotate-90
                    in-placement-right:rotate-90
                    in-placement-top:rotate-0
                    in-placement-bottom:rotate-180
                  `}
                >
                  <path d="M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z" />
                </svg>
              </AriaOverlayArrow>
            )}
          </div>
        )}
      </AriaTooltip>
    </AriaTooltipTrigger>
  )

  // Wrap with PortalProvider if a custom container is specified
  if (portalContainer?.current) {
    return (
      <UNSAFE_PortalProvider getContainer={() => portalContainer.current}>
        {tooltipContent}
      </UNSAFE_PortalProvider>
    )
  }

  return tooltipContent
}

export default Tooltip